import './Subscription.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRoute, useRouter } from 'react-router5';
import { incline } from 'lvovich';

import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { IcAngleRight16, IcSuccess72, IcWait72 } from '$assets';
import { balanceActions } from '$store/balance';
import { useCurrentLang, useTranslator, useUserInfo } from '$hooks';
import { contentPrioritySelector, mainActions, mainSelector } from '$store/main';
import { goBack } from '$navigation/helper';
import { authActions, authSelector } from '$store/auth';
import {
  CONTENT_PRIORITY,
  FEED,
  PROFILE,
  RESOLVE,
  SUBSCRIPTION_TONKEEPER,
} from '$shared/constants/pages';
import { classNames, rnbr, setStorageItem, statReachGoal, transliterate } from '$utils';
import { ContentPriorityType } from '$store/main/interface';
import { AdultPaywall } from './AdultPaywall/AdultPaywall';
import { AuthPopoverState, SubscriptionType } from '$store/models';
import { ContentCount, Loader, SubscriptionIcon, UnitName } from '$shared/components';
import { subscriptionsActions } from '$store/subscriptions';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { profileActions } from '$store/profile';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { setSubscriptionAuthor } from '$utils/subscriptionAuthor';

export const Subscription: FC<{ type?: SubscriptionType; pending?: boolean }> = ({
  type,
  pending,
}) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { decryptKeys, subscriptionPrice, subscriptionRubPrice } =
    useSelector(mainSelector);
  const router = useRouter();
  const route = useRoute();
  const contentPriority = useSelector(contentPrioritySelector);
  const [isLoading, setIsLoading] = useState(type === SubscriptionType.Author);
  const authorId = +route?.route.params.authorId;
  const hasSubscription =
    authorId > 0 ? decryptKeys[authorId] : type && decryptKeys[-type];
  const author = useUserInfo(authorId);
  const [price, setPrice] = useState({
    ton: subscriptionPrice,
    eur: subscriptionRubPrice,
  });
  const [cover, setCover] = useState<any>(null);
  const [sponsorsDescription, setSponsorsDescription] = useState('');
  const { isLogged } = useSelector(authSelector);
  const [contentCount, setContentCount] = useState({ photos: 0, videos: 0 });
  const currentLang = useCurrentLang();
  const isRU = currentLang.key === 'ru';

  useEffect(() => {
    if (type === SubscriptionType.Author) {
      statReachGoal('sponsor_subscription_view');
    } else if (type === SubscriptionType.Adult) {
      statReachGoal('adult_subscription_view');
    } else if (type === SubscriptionType.Media) {
      statReachGoal('media_subscription_view');
    }
  }, []);

  useEffect(() => {
    if (pending && type === SubscriptionType.Adult && hasSubscription) {
      statReachGoal('adult_subscription_fiat_bought');
    }
  }, [hasSubscription]);

  useEffect(() => {
    if (type === SubscriptionType.Media) {
      setStorageItem('contentPriorityType', ContentPriorityType.OTHER);
    } else if (type === SubscriptionType.Adult) {
      setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
    } else if (type === SubscriptionType.Author && author) {
      if (author.verified === 2) {
        setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
      } else {
        setStorageItem('contentPriorityType', ContentPriorityType.OTHER);
      }
    }
  }, [type, author]);

  useEffect(() => {
    Api.get(
      type === SubscriptionType.Author
        ? `/balance/subscription/author/${authorId}`
        : `/balance/subscription/common/${
            type === SubscriptionType.Adult ? 'adult' : 'media'
          }`,
    )
      .then((data: any) => {
        setSubscriptionAuthor(data.user);
        setPrice({
          ton: data.price,
          eur: data.eurPrice,
        });
        setSponsorsDescription(data.sponsorsDescription);
        if (data.coverUrl) {
          setCover({
            url: data.coverUrl,
            thumbnail: data.coverThumbnail,
          });
        }
        if (data.user) {
          dispatch(mainActions.setUser(data.user));
        }
        setContentCount({
          photos: data.photosCnt,
          videos: data.videosCnt,
        });
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [type, authorId]);

  useEffect(() => {
    if (
      !pending ||
      !isLogged ||
      hasSubscription ||
      !isLoading ||
      type !== SubscriptionType.Author
    ) {
      return;
    }
    dispatch(authActions.checkSubscriptionLoop());
    return () => {
      dispatch(authActions.stopCheckSubscriptionLoop());
    };
  }, [hasSubscription, pending, isLogged, isLoading, type]);

  const handleRefresh = useCallback(() => {
    location.reload();
  }, []);

  const handleGenerate = useCallback(() => {
    dispatch(
      balanceActions.generateInvoice({
        authorId: authorId,
        categoryId: type,
        onDone: (invoiceUrl) => {
          router.navigate(SUBSCRIPTION_TONKEEPER, {
            invoiceUrl,
          });
        },
      }),
    );
  }, [authorId, type]);

  const handleSubscription = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      statReachGoal('subscription_btn_click', {
        type,
      });
      if (type === SubscriptionType.Adult) {
        statReachGoal('adult_subscription_btn_click');
      }
      return;
    }

    if (authorId > 0 && author && author.verified === 2) {
      dispatch(
        balancePayActions.open({
          amount: +price.ton,
          action: BalancePayAction.SUBSCRIPTION_PERSONAL,
          objectId: authorId,
        }),
      );
    } else if (type === SubscriptionType.Adult) {
      dispatch(
        balancePayActions.open({
          amount: +price.eur,
          action: BalancePayAction.SUBSCRIPTION_ADULT,
          objectId: type,
        }),
      );
      // router.navigate(SUBSCRIPTION_FIAT, {
      //   category: 1,
      // });
    } else {
      dispatch(
        subscriptionsActions.subscriptionDialog({
          price: price.ton,
          authorId: authorId,
          categoryId: type,
        }),
      );
    }

    statReachGoal('subscription_btn_click', {
      type,
    });
    if (type === SubscriptionType.Author) {
      statReachGoal('sponsor_subscription_btn_click');
    } else if (type === SubscriptionType.Adult) {
      statReachGoal('adult_subscription_btn_click');
    } else if (type === SubscriptionType.Media) {
      statReachGoal('media_subscription_btn_click');
    }
  }, [authorId, type, price, author, isLogged]);

  useEffect(() => {
    if (isLoading || hasSubscription || price.eur <= 0) {
      return;
    }

    if (type !== SubscriptionType.Author || author) {
      // @ts-ignore
      if (isLogged && route?.route.params.open_pay && !window.isPayModalShown) {
        // @ts-ignore
        window.isPayModalShown = true;
        handleSubscription();
      }
    }
  }, [author, isLoading, hasSubscription, price]);

  const coverImage = useMemo(() => {
    if (!cover) {
      return null;
    }

    return cover.url && cover.thumbnail
      ? [cover.url, cover.thumbnail].map((url) => `url(${url})`).join(', ')
      : null;
  }, [cover]);

  const handleBack = useCallback(() => {
    goBack();
    if (type === SubscriptionType.Adult) {
      statReachGoal('adult_subscription_back');
    }
  }, [type]);

  const navigateToProfile = () => {
    router.navigate(PROFILE, { id: authorId });
  };

  const signInHandle = useCallback(() => {
    dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));

    statReachGoal('subscription_btn_click', {
      type,
    });
    if (type === SubscriptionType.Author) {
      statReachGoal('sponsor_subscription_login_click');
    } else if (type === SubscriptionType.Adult) {
      statReachGoal('adult_subscription_login_click');
    } else if (type === SubscriptionType.Media) {
      statReachGoal('media_subscription_login_click');
    }
  }, [type]);

  function renderSubscription() {
    if (type === SubscriptionType.Author) {
      return !isLoading ? (
        <>
          {cover && (
            <div
              className={classNames('Profile__cover', {
                active: true,
                visible: true,
                empty: false,
                Subscription__cover: true,
              })}
              style={coverImage ? { backgroundImage: coverImage } : undefined}
            />
          )}
          <div onClick={navigateToProfile} className="Subscription__icon_author">
            <SubscriptionIcon
              size="extra-large"
              type={SubscriptionType.Author}
              authorId={author.id}
            />
          </div>
          <div className="Subscription__title _authorTitle" onClick={navigateToProfile}>
            {t('subscription_private_title', {
              name: author.firstName + ' ' + author.lastName,
            })}
          </div>
          <div className="Subscription__caption">
            {t('subscription_private_caption', {
              name: isRU
                ? incline(
                    {
                      first: author.firstName ?? '',
                    },
                    'instrumental',
                  ).first
                : transliterate(author.firstName),
            })}
          </div>
          <div className="Subscription__content_count">
            <ContentCount photos={contentCount.photos} videos={contentCount.videos} />
          </div>
          {!!sponsorsDescription && (
            <div className="Subscription__sponsors_message_wrap">
              <div className="Subscription__sponsors_message">
                {rnbr(sponsorsDescription)}
              </div>
            </div>
          )}
        </>
      ) : (
        <Loader />
      );
    } else if (type === SubscriptionType.Media) {
      return (
        <>
          <SubscriptionIcon type={type} authorId={0} />
          <div className="Subscription__title">{t('subscription_media_title')}</div>
          <div className="Subscription__caption">
            {rnbr(t('subscription_media_caption'))}
          </div>
        </>
      );
    } else {
      return (
        <>
          <SubscriptionIcon type={SubscriptionType.Media} authorId={0} />
          <div className="Subscription__title">{t('subscription_title')}</div>
          <div className="Subscription__caption">{rnbr(t('subscription_caption'))}</div>
          <div className="Subscription__button">
            <Link routeName={CONTENT_PRIORITY}>
              <Button size={'small'} mode={'light'} iconRight={<IcAngleRight16 />}>
                {contentPriority === ContentPriorityType.ADULT
                  ? t('subscription_type_adult')
                  : t('subscription_type_media')}
              </Button>
            </Link>
          </div>
        </>
      );
    }
  }

  function renderContent() {
    if (pending && !hasSubscription) {
      return (
        <Placeholder
          centered
          title={t('subscription_activated_title')}
          iconSize={72}
          icon={<IcWait72 color="var(--tint-gold)" />}
          actions={
            <Button onClick={handleRefresh}>{t('subscription_activated_button')}</Button>
          }
        >
          {t('subscription_activated_caption')}
        </Placeholder>
      );
    }

    if (hasSubscription && type) {
      return (
        <Placeholder
          centered
          title={t('subscription_success_title')}
          icon={<IcSuccess72 />}
          actions={
            type === SubscriptionType.Author ? (
              <Link
                routeName={RESOLVE}
                routeParams={{ domain: 'id' + authorId }}
                routeOptions={{ tab: 'sponsored' }}
              >
                <Button>{t('subscription_success_button_go_to_posts')}</Button>
              </Link>
            ) : (
              <Link routeName={FEED}>
                <Button>{t('subscription_success_button_go_to_feed')}</Button>
              </Link>
            )
          }
        >
          {
            {
              [SubscriptionType.Adult]: t('subscription_success_adult_caption'),
              [SubscriptionType.Media]: t('subscription_success_media_caption'),
              [SubscriptionType.Author]:
                author &&
                t('subscription_success_author_caption', {
                  name: author?.firstName + ' ' + author?.lastName,
                }),
            }[type]
          }
        </Placeholder>
      );
    }

    if (type === SubscriptionType.Author && !price.ton) {
      return !isLoading ? (
        <Placeholder
          centered
          icon={
            <SubscriptionIcon
              size="large"
              type={SubscriptionType.Author}
              authorId={author.id}
            />
          }
          iconSize={82}
          title={<UnitName unit={author} />}
          actions={
            <Button onClick={handleBack} size="small">
              {t('back')}
            </Button>
          }
        >
          {t('subscription_private_failed_not_activated')}
        </Placeholder>
      ) : (
        <Loader isWrapped />
      );
    }

    return (
      <div
        className={classNames({
          Subscription: true,
          author: type === SubscriptionType.Author && !!cover,
        })}
      >
        <ScrollView>
          <div className="Subscription__cont">{renderSubscription()}</div>
        </ScrollView>
        <div className="Subscription__methods">
          {isLogged ? (
            <div className="Subscription__actionButton">
              <Button onClick={handleSubscription} flare>
                {authorId
                  ? t('subscription_author_buy_button')
                  : t('subscription_buy_button')}
              </Button>
            </div>
          ) : (
            <Button onClick={signInHandle} flare>
              {t('subscription_show_content_button')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (type === SubscriptionType.Adult && !hasSubscription && !pending) {
    return (
      <AdultPaywall
        handleGenerate={handleGenerate}
        handleSubscription={handleSubscription}
        subscriptionPrice={price.ton}
        subscriptionRubPrice={price.eur}
      />
    );
  }

  return (
    <>
      <Header showBackButton isTransparent />
      {renderContent()}
      <AuthPopoverSimple />
    </>
  );
};
